/**
 * Assistant chat state slice.
 *
 * Holds the active conversation: messages, the server-minted conversationId,
 * the most recent capacityRemaining snapshot, and request lifecycle flags.
 * One conversation in state at a time; "New chat" clears it.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  AssistantCapExceededError,
  sendAssistantMessage,
} from '../services/assistantApi'
import { isSessionExpired } from '../services/api'
import type {
  AssistantAttachment,
  CapacityRemaining,
  CapExceededErrorBody,
} from '../types/assistant'

// ============================================================================
// Types
// ============================================================================

export interface ChatMessage {
  /** Client-side id used as a React key; never leaves the client. */
  id: string
  role: 'user' | 'assistant'
  text: string
  attachments: AssistantAttachment[]
  /** Epoch millis when added to state. */
  timestamp: number
  /** Optimistic-pending flag for user messages awaiting a server reply. */
  pending?: boolean
}

interface AssistantState {
  conversationId: string | null
  messages: ChatMessage[]
  capacityRemaining: CapacityRemaining | null
  loading: boolean
  /** Generic error surfaced via Snackbar. Cap-exceeded uses its own field. */
  error: string | null
  /** Set when the server returns 429 LIMIT_EXCEEDED. */
  capError: CapExceededErrorBody | null
}

const initialState: AssistantState = {
  conversationId: null,
  messages: [],
  capacityRemaining: null,
  loading: false,
  error: null,
  capError: null,
}

// ============================================================================
// Thunks
// ============================================================================

/**
 * Send a user message. Optimistically appends the user turn so the input
 * clears immediately; on success appends the assistant reply, on failure
 * removes the optimistic user message and surfaces the error.
 */
export const sendMessage = createAsyncThunk<
  {
    userMessageId: string
    response: Awaited<ReturnType<typeof sendAssistantMessage>>
  },
  { text: string },
  { rejectValue: { kind: 'cap' | 'generic'; cap?: CapExceededErrorBody; message: string; userMessageId: string } }
>(
  'assistant/sendMessage',
  async ({ text }, { getState, rejectWithValue, dispatch }) => {
    const userMessageId = mintClientId()
    dispatch(appendUserMessage({ id: userMessageId, text }))

    const state = (getState() as { assistant: AssistantState }).assistant
    const conversationId = state.conversationId ?? undefined

    try {
      const response = await sendAssistantMessage({ message: text, conversationId })
      return { userMessageId, response }
    } catch (err) {
      // SessionExpiredError flows out of the axios interceptor; let the
      // session handler own that UX — don't write to error state here.
      if (isSessionExpired(err)) {
        return rejectWithValue({ kind: 'generic', message: '', userMessageId })
      }
      if (err instanceof AssistantCapExceededError) {
        return rejectWithValue({
          kind: 'cap',
          cap: err.body,
          message: err.message,
          userMessageId,
        })
      }
      const message = err instanceof Error ? err.message : 'Something went wrong sending your message.'
      return rejectWithValue({ kind: 'generic', message, userMessageId })
    }
  }
)

// ============================================================================
// Slice
// ============================================================================

const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    /** Optimistic append of the user's message before the server reply. */
    appendUserMessage(state, action: PayloadAction<{ id: string; text: string }>) {
      state.messages.push({
        id: action.payload.id,
        role: 'user',
        text: action.payload.text,
        attachments: [],
        timestamp: Date.now(),
        pending: true,
      })
    },
    startNewConversation(state) {
      state.conversationId = null
      state.messages = []
      state.error = null
      state.capError = null
      // capacityRemaining is preserved — it's a daily counter, not per-conversation
    },
    clearError(state) {
      state.error = null
      state.capError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { userMessageId, response } = action.payload
        // Clear the pending flag on the optimistic user message.
        const userMsg = state.messages.find((m) => m.id === userMessageId)
        if (userMsg) userMsg.pending = false

        // Persist conversationId once minted by the server.
        state.conversationId = response.conversationId

        // Append assistant reply.
        state.messages.push({
          id: mintClientId(),
          role: 'assistant',
          text: response.message.text,
          attachments: response.attachments,
          timestamp: Date.now(),
        })

        state.capacityRemaining = response.capacityRemaining
        state.loading = false
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false
        const payload = action.payload
        if (!payload) {
          state.error = action.error.message ?? 'Unexpected error.'
          return
        }
        // Roll back the optimistic user message on any failure.
        state.messages = state.messages.filter((m) => m.id !== payload.userMessageId)
        if (payload.kind === 'cap' && payload.cap) {
          state.capError = payload.cap
        } else if (payload.message) {
          state.error = payload.message
        }
      })
  },
})

export const { appendUserMessage, startNewConversation, clearError } = assistantSlice.actions
export default assistantSlice.reducer

// ============================================================================
// Helpers
// ============================================================================

function mintClientId(): string {
  return `msg_${Math.random().toString(36).slice(2, 11)}_${Date.now().toString(36)}`
}
